import { Action } from "redux"

import makeRequest from "../networkClient"
import { AsyncThunk, ReduxState } from "../common/types"

// Action Types
export const SET_PROJECT_RATE_TABLE_IDS = "SET_PROJECT_RATE_TABLE_IDS"

// Action Interfaces
export interface iSetProjectRateTableIdsAction extends Action {
    type: typeof SET_PROJECT_RATE_TABLE_IDS
    projectId: number
    rateTableIds: number[]
}

export type tByProjectCacheAction = iSetProjectRateTableIdsAction

// Action Creators
/**
 * Set the rate table IDs for a project in the cache.
 *
 * @param projectId - The project ID to cache rate table IDs for
 * @param rateTableIds - The rate table IDs associated with the project
 * @returns The action object
 */
export const setProjectRateTableIds = (
    projectId: number,
    rateTableIds: number[]
): iSetProjectRateTableIdsAction => ({
    type: SET_PROJECT_RATE_TABLE_IDS,
    projectId,
    rateTableIds,
})

// Track in-flight requests to prevent duplicate fetches
const pendingFetches = new Map<number, Promise<number[]>>()

// Async Thunk Actions
/**
 * Fetch rate table IDs for a project and cache the result in Redux state.
 * Returns cached data if available, deduplicates in-flight requests.
 *
 * @param projectId - The project ID to fetch rate table IDs for
 * @returns Promise resolving to the rate table IDs
 */
export const fetchProjectRateTableIds = (projectId: number): AsyncThunk<number[]> => async (dispatch, getState) => {
    const state: ReduxState = getState()
    const cached = state.byProjectCache[projectId]?.rateTableIds

    // Return cached data if available
    if (cached !== undefined) {
        return cached
    }

    // Return existing in-flight request if one exists
    const pending = pendingFetches.get(projectId)
    if (pending) {
        return pending
    }

    // Make the API request
    const fetchPromise = makeRequest({
        url: `/api/v4/projects/rate_table_ids/?project_id=${projectId}`,
        method: "GET",
    })
        .then((response: Record<string, number[]>) => {
            const rateTableIds = response[projectId] ?? []
            dispatch(setProjectRateTableIds(projectId, rateTableIds))
            pendingFetches.delete(projectId)
            return rateTableIds
        })
        .catch((error: Error) => {
            pendingFetches.delete(projectId)
            throw error
        })

    pendingFetches.set(projectId, fetchPromise)
    return fetchPromise
}

// State Type
/**
 * Cache data specific to a single project.
 */
export interface iProjectCacheData {
    rateTableIds: number[]
}

/**
 * Cache state keyed by project ID.
 * Stores project-specific data that can be reused across the application.
 */
export type tByProjectCacheState = {
    [projectId: number]: iProjectCacheData
}

// Initial State
const initialState: tByProjectCacheState = {}

// Reducer
/**
 * Reducer for the byProjectCache slice of state.
 * Manages project-specific cached data keyed by project ID.
 *
 * @param state - The current state
 * @param action - The action to process
 * @returns The new state
 */
const byProjectCache = (
    state: tByProjectCacheState = initialState,
    action: tByProjectCacheAction
): tByProjectCacheState => {
    switch (action.type) {
        case SET_PROJECT_RATE_TABLE_IDS:
            return {
                ...state,
                [action.projectId]: {
                    ...state[action.projectId],
                    rateTableIds: action.rateTableIds,
                },
            }
        default:
            return state
    }
}

export default byProjectCache
